import { uploadUrl } from "./assets.js";
import {
  SITE_BRAND,
  SITE_CONTACT,
  SITE_DEFAULT_OG_IMAGE,
  SITE_DEFAULT_OG_IMAGE_ALT,
  SITE_DEFAULT_OG_IMAGE_HEIGHT,
  SITE_DEFAULT_OG_IMAGE_TYPE,
  SITE_DEFAULT_OG_IMAGE_WIDTH,
  SITE_DEFAULT_TWITTER_IMAGE,
  SITE_DEFAULT_TWITTER_IMAGE_HEIGHT,
  SITE_DEFAULT_TWITTER_IMAGE_WIDTH,
  SITE_HOME_TITLE,
  SITE_LANGUAGE,
  SITE_LOCALE,
  SITE_NAME,
  SITE_OWNER,
  SITE_SOCIAL_PROFILES,
  SITE_TAGLINE,
  getSiteUrl,
} from "./siteConfig.js";

const MONTHS = {
  JAN: "01",
  FEB: "02",
  MAR: "03",
  APR: "04",
  MAY: "05",
  JUN: "06",
  JUL: "07",
  AUG: "08",
  SEP: "09",
  OCT: "10",
  NOV: "11",
  DEC: "12",
};

/**
 * @param {string} [path]
 */
export function absoluteUrl(path = "/") {
  const siteUrl = getSiteUrl();
  if (!path || path === "/") {
    return `${siteUrl}/`;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${siteUrl}${normalized}`;
}

/**
 * @param {string | undefined | null} title
 */
export function buildPageTitle(title) {
  const trimmed = String(title || "").trim();
  if (!trimmed || trimmed === SITE_NAME) {
    return `${SITE_NAME} — ${SITE_OWNER}`;
  }
  return `${trimmed} — ${SITE_NAME}`;
}

/**
 * Open Graph / Twitter title — page headline without the site-name suffix.
 *
 * @param {string | undefined | null} title
 */
export function buildSocialTitle(title) {
  const trimmed = String(title || "").trim();
  if (!trimmed || trimmed === SITE_NAME) {
    return SITE_HOME_TITLE;
  }
  return trimmed;
}

/**
 * @param {string | undefined | null} image
 */
export function resolveSeoImage(image) {
  const key = String(image || SITE_DEFAULT_OG_IMAGE).trim();
  if (!key) {
    return resolveSeoImage(SITE_DEFAULT_OG_IMAGE);
  }
  if (/^https?:\/\//.test(key)) {
    return key;
  }
  if (key.startsWith("og/")) {
    return absoluteUrl(`/${key}`);
  }
  const resolved = uploadUrl(key);
  if (/^https?:\/\//.test(resolved)) {
    return resolved;
  }
  return absoluteUrl(resolved.startsWith("/") ? resolved : `/${resolved}`);
}

/**
 * @param {string | undefined | null} image
 */
export function usesDefaultSocialImage(image) {
  const key = String(image || "").trim();
  return !key || key === SITE_DEFAULT_OG_IMAGE;
}

/**
 * @param {{
 *   image?: string,
 *   imageAlt?: string,
 *   imageWidth?: number,
 *   imageHeight?: number,
 *   imageType?: string,
 *   twitterImage?: string,
 *   twitterImageWidth?: number,
 *   twitterImageHeight?: number,
 *   title?: string,
 * }} input
 */
export function resolvePageSocialMeta(input) {
  const useDefaults = usesDefaultSocialImage(input.image);
  const imageAlt =
    input.imageAlt ||
    input.title ||
    (useDefaults ? SITE_DEFAULT_OG_IMAGE_ALT : SITE_NAME);

  return {
    imageUrl: resolveSeoImage(input.image),
    twitterImageUrl: resolveSeoImage(
      input.twitterImage || (useDefaults ? SITE_DEFAULT_TWITTER_IMAGE : input.image)
    ),
    imageAlt,
    imageWidth: input.imageWidth ?? (useDefaults ? SITE_DEFAULT_OG_IMAGE_WIDTH : undefined),
    imageHeight: input.imageHeight ?? (useDefaults ? SITE_DEFAULT_OG_IMAGE_HEIGHT : undefined),
    imageType: input.imageType ?? (useDefaults ? SITE_DEFAULT_OG_IMAGE_TYPE : undefined),
    twitterImageWidth:
      input.twitterImageWidth ??
      input.imageWidth ??
      (useDefaults && !input.twitterImage ? SITE_DEFAULT_TWITTER_IMAGE_WIDTH : undefined),
    twitterImageHeight:
      input.twitterImageHeight ??
      input.imageHeight ??
      (useDefaults && !input.twitterImage ? SITE_DEFAULT_TWITTER_IMAGE_HEIGHT : undefined),
  };
}

/**
 * @param {string} dateLabel
 */
export function parseDisplayDate(dateLabel) {
  const trimmed = String(dateLabel || "").trim();
  const fullMatch = trimmed.match(/^([A-Z]{3})\s+(\d{1,2}),\s+(\d{4})$/i);
  if (fullMatch) {
    const month = MONTHS[fullMatch[1].toUpperCase()];
    if (!month) {
      return undefined;
    }
    const day = fullMatch[2].padStart(2, "0");
    return `${fullMatch[3]}-${month}-${day}`;
  }

  const monthYearMatch = trimmed.match(/^([A-Z]{3})\s+(\d{4})$/i);
  if (monthYearMatch) {
    const month = MONTHS[monthYearMatch[1].toUpperCase()];
    if (!month) {
      return undefined;
    }
    return `${monthYearMatch[2]}-${month}-01`;
  }

  return undefined;
}

/**
 * @param {string} readLabel
 */
export function parseReadMinutes(readLabel) {
  const match = String(readLabel || "").match(/(\d+)\s*MIN/i);
  return match ? Number(match[1]) : undefined;
}

/**
 * @param {Array<{ name: string, path: string }>} items
 */
export function buildBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function buildWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${absoluteUrl("/")}#website`,
    url: absoluteUrl("/"),
    name: SITE_NAME,
    alternateName: SITE_BRAND,
    description: SITE_TAGLINE,
    inLanguage: SITE_LANGUAGE,
    publisher: {
      "@id": `${absoluteUrl("/")}#person`,
    },
  };
}

export function buildPersonJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${absoluteUrl("/")}#person`,
    name: SITE_OWNER,
    url: absoluteUrl("/about"),
    email: SITE_CONTACT.email,
    jobTitle: ["Photographer", "Storyteller", "Product Leader"],
    description: SITE_TAGLINE,
    sameAs: SITE_SOCIAL_PROFILES,
  };
}

/**
 * @param {{
 *   title: string,
 *   description: string,
 *   path: string,
 *   image?: string,
 * }} input
 */
export function buildWebPageJsonLd(input) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${absoluteUrl(input.path)}#webpage`,
    url: absoluteUrl(input.path),
    name: input.title,
    description: input.description,
    isPartOf: {
      "@id": `${absoluteUrl("/")}#website`,
    },
    about: {
      "@id": `${absoluteUrl("/")}#person`,
    },
    inLanguage: SITE_LANGUAGE,
    ...(input.image
      ? {
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: resolveSeoImage(input.image),
          },
        }
      : {}),
  };
}

/**
 * @param {{
 *   title: string,
 *   description: string,
 *   path: string,
 *   image?: string,
 *   datePublished?: string,
 *   dateModified?: string,
 *   section?: string,
 *   keywords?: string[],
 *   wordCount?: number,
 * }} input
 */
export function buildArticleJsonLd(input) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${absoluteUrl(input.path)}#article`,
    headline: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    image: [resolveSeoImage(input.image)],
    datePublished: input.datePublished,
    dateModified: input.dateModified || input.datePublished,
    author: {
      "@type": "Person",
      name: SITE_OWNER,
      url: absoluteUrl("/about"),
    },
    publisher: {
      "@type": "Person",
      name: SITE_OWNER,
      url: absoluteUrl("/about"),
    },
    mainEntityOfPage: {
      "@id": `${absoluteUrl(input.path)}#webpage`,
    },
    articleSection: input.section,
    keywords: input.keywords?.join(", "),
    inLanguage: SITE_LANGUAGE,
    ...(input.wordCount ? { wordCount: input.wordCount } : {}),
  };
}

/**
 * @param {{
 *   title: string,
 *   description: string,
 *   path: string,
 *   image?: string,
 *   genre?: string,
 * }} input
 */
export function buildCreativeWorkJsonLd(input) {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${absoluteUrl(input.path)}#creativework`,
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    image: resolveSeoImage(input.image),
    author: {
      "@type": "Person",
      name: SITE_OWNER,
      url: absoluteUrl("/about"),
    },
    creator: {
      "@type": "Person",
      name: SITE_OWNER,
    },
    genre: input.genre,
    inLanguage: SITE_LANGUAGE,
  };
}

/**
 * @param {{
 *   title: string,
 *   description: string,
 *   path: string,
 *   image?: string,
 *   collectionSize?: number,
 * }} input
 */
export function buildImageGalleryJsonLd(input) {
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "@id": `${absoluteUrl(input.path)}#gallery`,
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    image: resolveSeoImage(input.image),
    author: {
      "@type": "Person",
      name: SITE_OWNER,
    },
    ...(input.collectionSize ? { numberOfItems: input.collectionSize } : {}),
  };
}

export function buildContactPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${absoluteUrl("/contact")}#contactpage`,
    url: absoluteUrl("/contact"),
    name: "Contact CJ Jayamaha",
    description:
      "Get in touch with CJ Jayamaha for photography commissions, print orders, collaborations, licensing, and speaking enquiries.",
    mainEntity: {
      "@type": "Person",
      name: SITE_OWNER,
      email: SITE_CONTACT.email,
      url: absoluteUrl("/about"),
      sameAs: SITE_SOCIAL_PROFILES,
    },
  };
}

export { SITE_LOCALE };
